/** @file The review dialog (spec §9): plan, confidence, notes, ghost, pivot hints; Apply = one commit. */
import type { Doc } from '@pose2d/format/types';
import type { Translate } from '@pose2d/i18n/types';
import { Button } from '../../ui-kit/Button';
import { Dialog } from '../../ui-kit/Dialog';
import { actionOf } from './actionOf';
import type { AutorigSession, AutorigView } from './autorigTypes';
import { PivotHints } from './PivotHints';
import { planParts } from './planParts';
import { ReviewBody } from './ReviewBody';
import { usePivotHints } from './usePivotHints';

/** Props of ReviewDialog; it stays mounted, and its hints are re-seeded from every request under review. */
export interface ReviewDialogProps {
  t: Translate;
  view: AutorigView;
  session: Pick<AutorigSession, 'run' | 'apply' | 'close' | 'setGhost'>;
  doc: Doc;
}

/** Open while a plan is under review, as a non-modal side sheet so the ghost stays visible; Close and Escape are ignored while the commit runs. */
export function ReviewDialog({ t, view, session, doc }: ReviewDialogProps) {
  const [pivots, setPivots] = usePivotHints(view.kind === 'review' ? view.request : null);
  if (view.kind !== 'review') return null;
  const action = actionOf(view.request.fn);
  const hinted = action.pivots && Object.keys(pivots).length > 0;
  const close = (): void => { if (!view.applying) session.close(); };
  const footer = (
    <>
      <Button primary label={t('autorig.apply')} disabled={view.applying || view.reply.plan.commands.length === 0} onClick={() => { void session.apply(); }} />
      <Button label={t(hinted ? 'autorig.rerunWithPivots' : 'autorig.rerun')} disabled={view.applying} onClick={() => { void session.run({ ...view.request, pivots }); }} />
      <Button label={t('autorig.close')} disabled={view.applying} onClick={close} />
    </>
  );
  return (
    <Dialog open modal={false} title={t('autorig.reviewTitle', { fn: t(action.label) })} closeLabel={t('autorig.close')} onClose={close} footer={footer}>
      <ReviewBody t={t} view={view} session={session} />
      {action.pivots ? <PivotHints t={t} parts={planParts(doc, view.reply.plan)} pivots={pivots} onChange={setPivots} /> : null}
    </Dialog>
  );
}
